/* nimbus-salon-apply.js
 * Wizard + huisstijl voor het Shopify Forms-blok op de Certified Salon-aanvraagpagina.
 *
 * Het Forms-component (<shopify-forms-embed>) rendert in een OPEN shadow DOM.
 * Alle velden zijn directe children van <form> en elk control draagt een id of
 * name van de vorm `custom#<metafield_key>`. Die sleutel is taalonafhankelijk —
 * labels worden vertaald, sleutels niet — en is daarom de enige haak die we
 * gebruiken om velden te herkennen.
 *
 * Wat dit script doet, in volgorde:
 *   1. extra stylesheet in de shadow root (huisstijl + wizard)
 *   2. de velden hergroeperen in stappen, met voortgang en Vorige/Volgende
 *   3. per stap valideren vóór doorgaan (Forms zelf valideert pas bij verzenden)
 *   4. het adres geocoderen en in de verborgen lat/lng-velden zetten
 *   5. een live preview van de salonkaart en een samenvatting vóór verzenden
 *
 * Alles is additief: lukt stap 2 niet, dan blijft het gewone formulier staan.
 *
 * Hoort bij sections/nimbus-page-salon-apply.liquid.
 */
(function () {
  'use strict';

  var EMBED = 'shopify-forms-embed';
  var MARK = 'data-nsa-styled';
  var WIZ = 'data-nsa-wizard';
  var WATCH = 'data-nsa-watch';
  var POLL_MS = 100;
  /* 30 seconden: het Forms-blok laadt zijn eigen bundel plus hCaptcha en is
     op een trage verbinding makkelijk tien seconden onderweg. */
  var POLL_MAX = 300;

  /* ───────────────────────────────────────────────────────────────
     Configuratie

     STEPS bepaalt de volgorde en groepering. Velden die hier niet in
     staan belanden automatisch in de laatste stap, zodat een veld dat
     later in Shopify Forms wordt toegevoegd nooit stilletjes verdwijnt.

     REQUIRED spiegelt de "Required field"-vinkjes in Shopify Forms.
     Dat is helaas nergens uit de DOM af te lezen (de app zet geen
     required of aria-required), dus het staat hier expliciet. Wijzig je
     een veld in de app, wijzig het dan ook hier.
     ─────────────────────────────────────────────────────────────── */

  var STEPS = [
    { id: 'salon',   keys: ['sb_salon_name', 'sb_salon_bio', 'sb_salon_course', 'sb_business_type', 'sb_years_active', 'sb_team_size'] },
    { id: 'work',    keys: ['sb_treatments', 'sb_offers_brow_extensions', 'sb_uses_products', 'sb_products_used'] },
    { id: 'contact', keys: ['first_name', 'email', 'sb_salon_email', 'sb_salon_phone', 'sb_salon_website', 'sb_salon_booking'] },
    { id: 'address', keys: ['sb_salon_street', 'sb_salon_postal_code', 'sb_salon_city', 'sb_salon_state', 'sb_salon_country'] },
    { id: 'media',   keys: ['sb_salon_logo', 'sb_salon_photo', 'sb_work_photo', 'sb_salon_instagram', 'sb_salon_tiktok', 'sb_salon_facebook'] },
    { id: 'confirm', keys: ['sb_motivation', 'ownership'] }
  ];

  var REQUIRED = {
    first_name: true,
    email: true,
    sb_salon_name: true,
    sb_salon_bio: true,
    sb_salon_course: true,
    sb_salon_email: true,
    sb_salon_street: true,
    sb_salon_postal_code: true,
    sb_salon_city: true,
    sb_salon_country: true,
    sb_salon_logo: true,
    sb_business_type: true,
    sb_years_active: true,
    sb_team_size: true,
    sb_treatments: true,
    sb_offers_brow_extensions: true,
    sb_uses_products: true,
    sb_motivation: true,
    ownership: true
  };

  /* Velden die de wizard zelf invult en dus nooit aan de bezoeker toont. */
  var HIDDEN_KEYS = ['sb_salon_lat', 'sb_salon_lng'];

  /* Lichte vormcontrole op ingevulde waarden. Leeg = geen controle. */
  var SHAPE = {
    email: 'email',
    sb_salon_email: 'email',
    sb_salon_website: 'url',
    sb_salon_booking: 'url',
    sb_salon_facebook: 'url'
  };

  /* Engelse fallbacks; de sectie overschrijft deze via data-attributen. */
  var TEXT = {
    next: 'Continue',
    back: 'Back',
    stepOf: 'Step {n} of {total}',
    required: 'Please fill in this field.',
    requiredChoice: 'Please choose an option.',
    requiredFile: 'Please add a file.',
    requiredConsent: 'Please confirm this to continue.',
    badEmail: 'Please enter a valid email address.',
    badUrl: 'Please enter a valid link, starting with https://',
    geoChecking: 'Looking up your location…',
    geoFound: 'Location found: {place}',
    geoFailed: 'We could not pin your address on the map. You can continue — we will place you by hand.',
    previewTitle: 'Your listing',
    previewHint: 'This is roughly how clients will see you on the salon locator.',
    previewName: 'Your salon',
    previewPlace: 'Your city',
    hintHandle: 'Your username only — not the full link.',
    hintUrl: 'The full link to your page, starting with https://',
    summaryTitle: 'Check your application',
    summaryEdit: 'Edit',
    stepTitles: {
      salon: 'Your salon',
      work: 'What you do',
      contact: 'Contact',
      address: 'Address',
      media: 'Images & social',
      confirm: 'Confirm'
    },
    stepTexts: {
      salon: '', work: '', contact: '', address: '', media: '', confirm: ''
    }
  };

  /* ───────────────────────────────────────────────────────────────
     Stylesheet voor in de shadow root
     ─────────────────────────────────────────────────────────────── */

  var SHEET = [
    /* ── Basis: kop, formulier, knop ── */
    'h2[class] { text-transform: uppercase; letter-spacing: -0.02em; margin-bottom: 0.6rem; }',
    /* De velden tekenen hun rand als een NAAR BUITEN staande box-shadow van
       1.5px, en section._formContainer_ heeft overflow: hidden. Zonder deze
       marge snijdt die container de randen aan de boven- en zijkanten af. */
    'form[class] { row-gap: 1.6rem; padding: 2px; }',

    /* Zelfde maatvoering als .nsa-wz-btn, zodat Verzenden en Vorige even groot
       zijn. box-sizing expliciet: met content-box telde de app de horizontale
       padding bij de breedte op, waardoor de knop op mobiel buiten de kaart
       viel. En geen min-width meer — die maakte hem onnodig breed. */
    'button[type="submit"] { box-sizing: border-box; width: auto; min-width: 0; margin: 0; padding: 0 3.2rem; align-items: center; justify-content: center; font-family: var(--nsa-ff-display, "Object Sans", system-ui, sans-serif); font-size: 1.2rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; min-height: 5.2rem; box-shadow: inset 0 0 0 1.5px var(--button-background-color); transition: background-color 0.3s ease, color 0.3s ease, transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1); }',
    'button[type="submit"]:hover, button[type="submit"]:focus-visible { background-color: transparent; color: var(--forms-accent-color); filter: none; transform: translate3d(0, -2px, 0); }',
    'button[type="submit"]:disabled { opacity: 0.6; transform: none; }',
    /* De app geeft knoppen en veldcontainers display: flex, wat het
       [hidden]-attribuut overrulet. Daarom expliciet. */
    'button[type="submit"][hidden] { display: none; }',
    '[class*="_formFieldContainer_"][hidden] { display: none; }',

    /* ── Bestandsvelden ── */
    '[class*="_formFileInputField_"] { border: 1.5px dashed var(--nsa-hair-2, rgba(26, 19, 16, 0.12)); border-radius: var(--form-field-border-radius); }',
    '[class*="_formFileInputField_"]:has(:focus-visible) { box-shadow: var(--form-field-border-focused); }',
    '[class*="_formFileInputButton_"] { background: var(--nsa-sand, #f5eee4); color: var(--nsa-espresso, #1a1310); border-radius: 999px; padding: 0.8rem 1.6rem; font-family: var(--nsa-ff-display, "Object Sans", system-ui, sans-serif); font-size: 1.05rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; }',
    '[class*="_formFileInputButton_"]:hover { background: var(--forms-accent-color); color: var(--nsa-espresso, #1a1310); }',

    '[class*="_formDisclaimer_"] { font-size: 1.2rem; line-height: 1.6; margin-top: 1.6rem; }',

    /* ── Keuzevelden als chips ── */
    'fieldset[class*="_radioFieldSet_"], fieldset[class*="_checkboxFieldSet_"] { border: 0; padding: 0; margin: 0; }',
    'legend[class] { font-family: var(--nsa-ff-display, system-ui, sans-serif); font-size: 1.15rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--nsa-taupe, #8c7867); margin-bottom: 1.2rem; padding: 0; }',
    /* De app zet hier flex-direction: column ("verticalLayout"); wij willen
       chips die naast elkaar doorlopen, dus de richting expliciet terug. */
    '[class*="_verticalLayout_"], fieldset[class*="_checkboxFieldSet_"] > div { display: flex; flex-direction: row; flex-wrap: wrap; gap: 0.8rem; }',

    'label[class*="_radioField_"], label[class*="_checkboxWrapper_"] { display: inline-flex; align-items: center; gap: 0.8rem; margin: 0; padding: 1.1rem 1.8rem; border: 1.5px solid var(--nsa-hair, #e8dfd1); border-radius: 999px; background: transparent; cursor: pointer; line-height: 1.2; transition: border-color 0.25s ease, background-color 0.25s ease, transform 0.25s ease; }',
    'label[class*="_radioField_"]:hover, label[class*="_checkboxWrapper_"]:hover { border-color: var(--nsa-pink, #ff8dbb); transform: translate3d(0, -1px, 0); }',
    'label[class*="_radioField_"]:has(:checked), label[class*="_checkboxWrapper_"]:has(:checked) { border-color: var(--nsa-pink, #ff8dbb); background: color-mix(in srgb, var(--nsa-pink, #ff8dbb) 14%, transparent); }',
    'label[class*="_radioField_"]:has(:focus-visible), label[class*="_checkboxWrapper_"]:has(:focus-visible) { outline: 2px solid var(--nsa-pink, #ff8dbb); outline-offset: 2px; }',
    'input[type="radio"][class], label[class*="_checkboxWrapper_"] input[type="checkbox"] { width: 1.4rem; height: 1.4rem; accent-color: var(--nsa-pink, #ff8dbb); flex: none; }',

    /* De instemmingsvraag blijft een gewone regel, geen chip. */
    '.nsa-wz-step--confirm label[class*="_checkboxWrapper_"] { border: 0; border-radius: 0; padding: 0; align-items: flex-start; gap: 1.2rem; }',
    '.nsa-wz-step--confirm label[class*="_checkboxWrapper_"]:hover { background: transparent; transform: none; }',
    '.nsa-wz-step--confirm label[class*="_checkboxWrapper_"]:has(:checked) { background: transparent; }',

    /* ── Wizard-chroom ── */
    '.nsa-wz { display: flex; flex-direction: column; gap: 2.4rem; }',

    '.nsa-wz-rail { display: flex; align-items: center; gap: 0.6rem; }',
    '.nsa-wz-rail__item { flex: 1 1 0; height: 0.4rem; border-radius: 999px; background: var(--nsa-hair, #e8dfd1); overflow: hidden; }',
    '.nsa-wz-rail__item span { display: block; height: 100%; width: 0; border-radius: 999px; background: var(--nsa-pink, #ff8dbb); transition: width 0.5s var(--nsa-ease, cubic-bezier(0.22, 1, 0.36, 1)); }',
    '.nsa-wz-rail__item--done span, .nsa-wz-rail__item--current span { width: 100%; }',

    '.nsa-wz-head { display: flex; flex-direction: column; gap: 0.4rem; }',
    '.nsa-wz-head__count { font-family: var(--nsa-ff-display, system-ui, sans-serif); font-size: 1.05rem; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: var(--nsa-pink, #ff8dbb); }',
    '.nsa-wz-head__title { font-family: var(--nsa-ff-display, system-ui, sans-serif); font-size: clamp(2rem, 3vw, 2.6rem); font-weight: 700; letter-spacing: -0.02em; text-transform: uppercase; color: var(--nsa-espresso, #1a1310); margin: 0; }',
    '.nsa-wz-head__text { font-size: 1.4rem; line-height: 1.6; color: var(--nsa-taupe, #8c7867); margin: 0; }',

    '.nsa-wz-steps { position: relative; }',
    '.nsa-wz-step { display: flex; flex-direction: column; gap: 1.8rem; }',
    /* Inactieve stappen blijven in de DOM en focusbaar-vrij, maar nemen geen ruimte. */
    '.nsa-wz-step[hidden] { display: none; }',

    /* De velden komen één voor één binnen in plaats van het hele paneel in
       één keer; --nsa-i wordt per kind in JS gezet. */
    '.nsa-wz-step--entering > * { animation: nsa-wz-rise 0.5s var(--nsa-ease, cubic-bezier(0.22, 1, 0.36, 1)) both; animation-delay: calc(var(--nsa-i, 0) * 55ms); }',
    '@keyframes nsa-wz-rise { from { opacity: 0; transform: translate3d(0, 1.4rem, 0); } to { opacity: 1; transform: none; } }',
    '.nsa-wz-head--in { animation: nsa-wz-rise 0.45s var(--nsa-ease, cubic-bezier(0.22, 1, 0.36, 1)) both; }',

    /* Chip die aangeklikt wordt veert even op. In de bevestigingsstap niet,
       daar is het vinkje een gewone regel. */
    '.nsa-wz-step:not(.nsa-wz-step--confirm) label[class*="_radioField_"]:has(:checked), .nsa-wz-step:not(.nsa-wz-step--confirm) label[class*="_checkboxWrapper_"]:has(:checked) { animation: nsa-wz-pop 0.32s var(--nsa-ease, cubic-bezier(0.22, 1, 0.36, 1)); }',
    '@keyframes nsa-wz-pop { 0% { transform: scale(1); } 45% { transform: scale(1.06); } 100% { transform: scale(1); } }',

    /* De lopende balk krijgt een zachte verloopbeweging. */
    '.nsa-wz-rail__item--current span { background: linear-gradient(90deg, var(--nsa-pink, #ff8dbb), var(--nsa-caramel, rgba(196, 154, 108, 0.9)), var(--nsa-pink, #ff8dbb)); background-size: 200% 100%; animation: nsa-wz-flow 2.6s linear infinite; }',
    '@keyframes nsa-wz-flow { from { background-position: 0 0; } to { background-position: 200% 0; } }',

    /* De preview wipt kort op zodra er iets verandert. */
    '.nsa-wz-preview--bump { animation: nsa-wz-bump 0.4s var(--nsa-ease, cubic-bezier(0.22, 1, 0.36, 1)); }',
    '@keyframes nsa-wz-bump { 0% { transform: scale(1); } 40% { transform: scale(1.012); } 100% { transform: scale(1); } }',

    '@media (prefers-reduced-motion: reduce) {',
    '  .nsa-wz-step--entering > *, .nsa-wz-head--in, .nsa-wz-preview--bump { animation: none; }',
    '  .nsa-wz-rail__item--current span { animation: none; background: var(--nsa-pink, #ff8dbb); }',
    '  .nsa-wz-step label[class*="_radioField_"]:has(:checked), .nsa-wz-step label[class*="_checkboxWrapper_"]:has(:checked) { animation: none; }',
    '  .nsa-wz-rail__item span { transition: none; }',
    '}',

    '.nsa-wz-nav { display: flex; align-items: center; gap: 1.2rem; flex-wrap: wrap; }',
    '.nsa-wz-nav__spacer { flex: 1 1 auto; }',
    '.nsa-wz-btn { appearance: none; border: 1.5px solid var(--nsa-espresso, #1a1310); background: var(--nsa-espresso, #1a1310); color: var(--nsa-cream, #fbf7f2); border-radius: 999px; min-height: 5.2rem; padding: 0 3.2rem; font-family: var(--nsa-ff-display, system-ui, sans-serif); font-size: 1.2rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer; transition: background-color 0.3s ease, color 0.3s ease, transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1); }',
    '.nsa-wz-btn:hover, .nsa-wz-btn:focus-visible { background: transparent; color: var(--nsa-espresso, #1a1310); transform: translate3d(0, -2px, 0); }',
    '.nsa-wz-btn--ghost { background: transparent; color: var(--nsa-espresso, #1a1310); border-color: var(--nsa-hair, #e8dfd1); }',
    '.nsa-wz-btn--ghost:hover, .nsa-wz-btn--ghost:focus-visible { border-color: var(--nsa-espresso, #1a1310); }',

    /* ── Veldfouten ── */
    '.nsa-wz-hint { display: block; margin-top: 0.6rem; font-size: 1.2rem; line-height: 1.4; color: var(--nsa-taupe, #8c7867); }',
    '.nsa-wz-err { display: block; margin-top: 0.6rem; font-size: 1.2rem; line-height: 1.4; color: #b4315a; animation: nsa-wz-rise 0.3s var(--nsa-ease, cubic-bezier(0.22, 1, 0.36, 1)) both; }',
    '.nsa-wz-invalid [class*="_formInputField_"], .nsa-wz-invalid [class*="_textArea_"] { box-shadow: 0 0 0 1.5px #b4315a; }',
    '.nsa-wz-invalid { animation: nsa-wz-shake 0.45s both; }',
    '@keyframes nsa-wz-shake { 0%, 100% { transform: translate3d(0, 0, 0); } 20% { transform: translate3d(-5px, 0, 0); } 40% { transform: translate3d(5px, 0, 0); } 60% { transform: translate3d(-3px, 0, 0); } 80% { transform: translate3d(3px, 0, 0); } }',
    '@media (prefers-reduced-motion: reduce) { .nsa-wz-invalid, .nsa-wz-err { animation: none; } }',

    /* ── Geocoding ── */
    '.nsa-wz-geo { display: flex; align-items: flex-start; gap: 0.8rem; font-size: 1.3rem; line-height: 1.5; color: var(--nsa-taupe, #8c7867); min-height: 2rem; }',
    '.nsa-wz-geo--found { color: var(--nsa-espresso, #1a1310); }',
    '.nsa-wz-geo__dot { flex: none; width: 0.8rem; height: 0.8rem; margin-top: 0.55rem; border-radius: 50%; background: var(--nsa-hair, #e8dfd1); }',
    '.nsa-wz-geo--busy .nsa-wz-geo__dot { background: var(--nsa-caramel, rgba(196, 154, 108, 0.9)); animation: nsa-wz-pulse 1.1s ease-in-out infinite; }',
    '.nsa-wz-geo--found .nsa-wz-geo__dot { background: var(--nsa-pink, #ff8dbb); animation: nsa-wz-pop 0.45s var(--nsa-ease, cubic-bezier(0.22, 1, 0.36, 1)); }',
    '@keyframes nsa-wz-pulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }',
    '@media (prefers-reduced-motion: reduce) { .nsa-wz-geo--busy .nsa-wz-geo__dot { animation: none; } }',

    /* ── Live preview van de salonkaart ── */
    '.nsa-wz-preview { border: 1px solid var(--nsa-hair, #e8dfd1); border-radius: var(--nsa-r-outer, 2.4rem); padding: 2rem; background: var(--nsa-cream, #fbf7f2); display: flex; flex-direction: column; gap: 1.4rem; }',
    '.nsa-wz-preview__cap { font-family: var(--nsa-ff-display, system-ui, sans-serif); font-size: 1.05rem; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: var(--nsa-taupe, #8c7867); }',
    '.nsa-wz-card { display: flex; gap: 1.6rem; align-items: center; }',
    '.nsa-wz-card__logo { flex: none; width: 6.4rem; height: 6.4rem; border-radius: 50%; background: var(--nsa-sand, #f5eee4); background-size: cover; background-position: center; display: grid; place-items: center; font-family: var(--nsa-ff-serif, Georgia, serif); font-size: 2.4rem; color: var(--nsa-taupe, #8c7867); overflow: hidden; }',
    '.nsa-wz-card__body { min-width: 0; display: flex; flex-direction: column; gap: 0.4rem; }',
    '.nsa-wz-card__name { font-family: var(--nsa-ff-display, system-ui, sans-serif); font-size: 1.8rem; font-weight: 700; letter-spacing: -0.01em; color: var(--nsa-espresso, #1a1310); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }',
    '.nsa-wz-card__place { font-size: 1.35rem; color: var(--nsa-taupe, #8c7867); }',
    '.nsa-wz-card__tags { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.3rem; }',
    '.nsa-wz-card__tag { font-size: 1.1rem; line-height: 1; padding: 0.5rem 0.9rem; border-radius: 999px; background: var(--nsa-sand, #f5eee4); color: var(--nsa-espresso, #1a1310); }',
    '.nsa-wz-card--ghost { opacity: 0.55; }',
    '.nsa-wz-preview__hint { font-size: 1.25rem; line-height: 1.5; color: var(--nsa-taupe, #8c7867); }',

    /* ── Samenvatting ── */
    '.nsa-wz-sum { display: flex; flex-direction: column; gap: 0; border-top: 1px solid var(--nsa-hair, #e8dfd1); }',
    '.nsa-wz-sum__row { display: flex; gap: 1.6rem; align-items: baseline; padding: 1.1rem 0; border-bottom: 1px solid var(--nsa-hair, #e8dfd1); }',
    '.nsa-wz-sum__k { flex: 0 0 38%; font-size: 1.2rem; letter-spacing: 0.06em; text-transform: uppercase; color: var(--nsa-taupe, #8c7867); }',
    '.nsa-wz-sum__v { flex: 1 1 auto; font-size: 1.4rem; color: var(--nsa-espresso, #1a1310); overflow-wrap: anywhere; }',
    '.nsa-wz-sum__v--empty { color: var(--nsa-taupe, #8c7867); font-style: italic; }',
    '.nsa-wz-sum__edit { appearance: none; border: 0; background: none; padding: 0; font: inherit; font-size: 1.2rem; color: var(--nsa-pink, #ff8dbb); text-decoration: underline; cursor: pointer; flex: none; }',

    '@media (max-width: 749px) {',
    '  .nsa-wz-btn { width: 100%; padding: 0 2rem; }',
    '  .nsa-wz-nav { flex-direction: column-reverse; align-items: stretch; }',
    '  .nsa-wz-nav__spacer { display: none; }',
    '  .nsa-wz-sum__row { flex-direction: column; gap: 0.4rem; }',
    '  .nsa-wz-sum__k { flex: none; }',
    '  button[type="submit"] { width: 100%; padding: 0 2rem; }',
    '}'
  ].join('\n');

  function buildSheet() {
    var sheet = new CSSStyleSheet();
    sheet.replaceSync(SHEET);
    return sheet;
  }

  /* ───────────────────────────────────────────────────────────────
     Hulpjes
     ─────────────────────────────────────────────────────────────── */

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : m;
    });
  }

  /* De sleutel van een veldblok: `custom#sb_salon_city` → `sb_salon_city`.
     Fieldsets dragen de naam zelf; losse velden via het eerste control. */
  function keyOfBlock(block) {
    var raw = block.getAttribute('name') || '';
    if (!raw) {
      var ctrl = block.querySelector('input, textarea, select');
      if (!ctrl) return '';
      raw = ctrl.getAttribute('name') || ctrl.id || '';
    }
    return raw.replace(/^custom#/, '').replace(/-field$/, '');
  }

  function isFieldBlock(node) {
    if (node.tagName === 'BUTTON') return false;
    var ctrl = node.querySelector ? node.querySelector('input, textarea, select') : null;
    if (!ctrl) return false;
    if (ctrl.name === 'h-captcha-response') return false;
    return !!keyOfBlock(node);
  }

  function labelOf(block) {
    var lab = block.querySelector('legend, label[class*="_formInputFieldLabel_"], p[class*="_textBody_"]');
    return lab ? lab.textContent.trim() : '';
  }

  /* Waarde van een veldblok als leesbare tekst (ook voor de samenvatting). */
  function valueOf(block) {
    var checks = block.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    if (checks.length) {
      var picked = [];
      Array.prototype.forEach.call(checks, function (c) {
        if (!c.checked) return;
        var span = c.parentElement && c.parentElement.querySelector('span');
        picked.push(span ? span.textContent.trim() : c.value);
      });
      return picked.join(', ');
    }
    var file = block.querySelector('input[type="file"]');
    if (file) return file.files && file.files.length ? file.files[0].name : '';
    var ctrl = block.querySelector('input, textarea, select');
    return ctrl ? String(ctrl.value || '').trim() : '';
  }

  function isFilled(block) {
    return valueOf(block) !== '';
  }

  function setNativeValue(input, value) {
    var proto = input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement : window.HTMLInputElement;
    var desc = Object.getOwnPropertyDescriptor(proto.prototype, 'value');
    if (desc && desc.set) desc.set.call(input, value);
    else input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  var RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var RE_SCHEME = /^https?:\/\//i;

  function looksLikeUrl(value) {
    try {
      var u = new URL(RE_SCHEME.test(value) ? value : 'https://' + value);
      return !!u.hostname && u.hostname.indexOf('.') > 0;
    } catch (e) {
      return false;
    }
  }

  /*
    De metaobject-velden website, appointment_url en facebook zijn van het type
    `url`; Shopify weigert een waarde zonder protocol. Klanten typen vaak alleen
    het domein, dus vullen we het protocol aan zodra het veld geldig is.
  */
  function withScheme(value) {
    return RE_SCHEME.test(value) ? value : 'https://' + value;
  }

  /* ───────────────────────────────────────────────────────────────
     Wizard
     ─────────────────────────────────────────────────────────────── */

  function Wizard(root, host) {
    this.root = root;
    this.host = host;
    this.form = root.querySelector('form');
    this.text = this.readText(host);
    this.blocks = {};
    this.steps = [];
    this.index = 0;
    this.geo = { lat: '', lng: '', place: '', signature: '' };
  }

  Wizard.prototype.readText = function (host) {
    var out = JSON.parse(JSON.stringify(TEXT));
    if (!host) return out;
    var d = host.dataset || {};
    Object.keys(out).forEach(function (k) {
      if (k === 'stepTitles' || k === 'stepTexts') return;
      if (d[k]) out[k] = d[k];
    });
    STEPS.forEach(function (s) {
      var t = d['title' + s.id.charAt(0).toUpperCase() + s.id.slice(1)];
      var x = d['text' + s.id.charAt(0).toUpperCase() + s.id.slice(1)];
      if (t) out.stepTitles[s.id] = t;
      if (x) out.stepTexts[s.id] = x;
    });
    return out;
  };

  Wizard.prototype.build = function () {
    var form = this.form;
    if (!form || form.hasAttribute(WIZ)) return false;

    var self = this;
    var children = Array.prototype.slice.call(form.children);
    var fieldBlocks = children.filter(isFieldBlock);
    if (fieldBlocks.length < 4) return false;

    fieldBlocks.forEach(function (b) { self.blocks[keyOfBlock(b)] = b; });

    this.submit = form.querySelector('button[type="submit"]');
    if (!this.submit) return false;

    /* Verborgen velden uit beeld halen, maar in het formulier laten. */
    HIDDEN_KEYS.forEach(function (k) {
      var b = self.blocks[k];
      if (b) b.hidden = true;
    });

    var shell = el('div', 'nsa-wz');
    var rail = el('div', 'nsa-wz-rail');
    var head = el('div', 'nsa-wz-head');
    var count = el('span', 'nsa-wz-head__count');
    var title = el('h3', 'nsa-wz-head__title');
    var intro = el('p', 'nsa-wz-head__text');
    head.appendChild(count);
    head.appendChild(title);
    head.appendChild(intro);

    var stage = el('div', 'nsa-wz-steps');
    var nav = el('div', 'nsa-wz-nav');
    var back = el('button', 'nsa-wz-btn nsa-wz-btn--ghost', this.text.back);
    back.type = 'button';
    var spacer = el('span', 'nsa-wz-nav__spacer');
    var next = el('button', 'nsa-wz-btn', this.text.next);
    next.type = 'button';
    nav.appendChild(back);
    nav.appendChild(spacer);
    nav.appendChild(next);

    /* Stappen vullen; onbekende velden gaan naar de laatste stap. */
    var placed = {};
    STEPS.forEach(function (def) {
      var panel = el('div', 'nsa-wz-step nsa-wz-step--' + def.id);
      panel.hidden = true;
      def.keys.forEach(function (k) {
        var b = self.blocks[k];
        if (!b) return;
        panel.appendChild(b);
        placed[k] = true;
      });
      stage.appendChild(panel);
      self.steps.push({ def: def, panel: panel });
    });

    var last = this.steps[this.steps.length - 1];
    fieldBlocks.forEach(function (b) {
      var k = keyOfBlock(b);
      if (placed[k] || HIDDEN_KEYS.indexOf(k) !== -1) return;
      last.panel.appendChild(b);
    });
    HIDDEN_KEYS.forEach(function (k) {
      if (self.blocks[k]) last.panel.appendChild(self.blocks[k]);
    });

    this.buildHints();
    this.buildGeo();
    this.buildPreview();
    this.buildSummary();

    shell.appendChild(rail);
    shell.appendChild(head);
    if (this.preview) shell.appendChild(this.preview);
    shell.appendChild(stage);
    shell.appendChild(nav);

    form.insertBefore(shell, this.submit);
    last.panel.appendChild(this.summary);
    nav.insertBefore(this.submit, next);

    this.rail = rail;
    this.head = head;
    this.count = count;
    this.title = title;
    this.intro = intro;
    this.nextBtn = next;
    this.backBtn = back;

    for (var i = 0; i < this.steps.length; i++) {
      var seg = el('span', 'nsa-wz-rail__item');
      seg.appendChild(el('span'));
      rail.appendChild(seg);
    }

    next.addEventListener('click', function () { self.go(self.index + 1); });
    back.addEventListener('click', function () { self.go(self.index - 1); });
    form.addEventListener('input', function () { self.onInput(); });
    form.addEventListener('change', function () { self.onInput(); });

    /* Tweede kans: vlak vóór verzenden de coördinaten nog eens zetten. */
    form.addEventListener('submit', function () { self.applyGeo(); }, true);

    form.setAttribute(WIZ, '');
    this.show(0);
    return true;
  };

  /* De salonkaart bouwt de Instagram- en TikTok-link zélf op uit een
     gebruikersnaam (instagram.com/<naam>), terwijl Facebook as-is wordt
     gebruikt. Plakt iemand bij Instagram een volledige URL, dan ontstaat er een
     kapotte link. Daarom staat er nu onder elk veld wat er verwacht wordt. */
  Wizard.prototype.buildHints = function () {
    var self = this;
    var hints = {
      sb_salon_instagram: this.text.hintHandle,
      sb_salon_tiktok: this.text.hintHandle,
      sb_salon_facebook: this.text.hintUrl
    };
    Object.keys(hints).forEach(function (key) {
      var block = self.blocks[key];
      if (!block || !hints[key] || block.querySelector('.nsa-wz-hint')) return;
      block.appendChild(el('span', 'nsa-wz-hint', hints[key]));
    });
  };

  Wizard.prototype.buildGeo = function () {
    var box = el('div', 'nsa-wz-geo');
    box.appendChild(el('span', 'nsa-wz-geo__dot'));
    box.appendChild(el('span', 'nsa-wz-geo__msg'));
    this.geoBox = box;
    var step = this.steps.filter(function (s) { return s.def.id === 'address'; })[0];
    if (step) step.panel.appendChild(box);
  };

  Wizard.prototype.buildPreview = function () {
    var wrap = el('div', 'nsa-wz-preview');
    wrap.appendChild(el('span', 'nsa-wz-preview__cap', this.text.previewTitle));
    var card = el('div', 'nsa-wz-card nsa-wz-card--ghost');
    var logo = el('span', 'nsa-wz-card__logo');
    var body = el('div', 'nsa-wz-card__body');
    var name = el('span', 'nsa-wz-card__name');
    var place = el('span', 'nsa-wz-card__place');
    var tags = el('div', 'nsa-wz-card__tags');
    body.appendChild(name);
    body.appendChild(place);
    body.appendChild(tags);
    card.appendChild(logo);
    card.appendChild(body);
    wrap.appendChild(card);
    wrap.appendChild(el('span', 'nsa-wz-preview__hint', this.text.previewHint));
    this.preview = wrap;
    this.pv = { card: card, logo: logo, name: name, place: place, tags: tags };
    this.renderPreview();
  };

  Wizard.prototype.buildSummary = function () {
    var self = this;
    var wrap = el('div');
    wrap.appendChild(el('span', 'nsa-wz-preview__cap', this.text.summaryTitle));
    var list = el('div', 'nsa-wz-sum');
    wrap.appendChild(list);
    this.summary = wrap;
    this.summaryList = list;
    this.summaryRows = [];

    STEPS.forEach(function (def, stepIndex) {
      if (def.id === 'confirm') return;
      def.keys.forEach(function (k) {
        var b = self.blocks[k];
        if (!b) return;
        var row = el('div', 'nsa-wz-sum__row');
        row.appendChild(el('span', 'nsa-wz-sum__k', labelOf(b) || k));
        var v = el('span', 'nsa-wz-sum__v');
        row.appendChild(v);
        var edit = el('button', 'nsa-wz-sum__edit', self.text.summaryEdit);
        edit.type = 'button';
        edit.addEventListener('click', function () { self.go(stepIndex, true); });
        row.appendChild(edit);
        list.appendChild(row);
        self.summaryRows.push({ block: b, valueEl: v });
      });
    });
  };

  Wizard.prototype.renderSummary = function () {
    this.summaryRows.forEach(function (r) {
      var v = valueOf(r.block);
      r.valueEl.textContent = v || '—';
      r.valueEl.classList.toggle('nsa-wz-sum__v--empty', !v);
    });
  };

  Wizard.prototype.renderPreview = function () {
    if (!this.pv) return;
    var name = this.blocks.sb_salon_name ? valueOf(this.blocks.sb_salon_name) : '';
    var city = this.blocks.sb_salon_city ? valueOf(this.blocks.sb_salon_city) : '';
    var country = this.blocks.sb_salon_country ? valueOf(this.blocks.sb_salon_country) : '';
    var treatments = this.blocks.sb_treatments ? valueOf(this.blocks.sb_treatments) : '';

    this.pv.name.textContent = name || this.text.previewName;
    var place = [city, country].filter(Boolean).join(', ');
    this.pv.place.textContent = place || this.text.previewPlace;
    this.pv.card.classList.toggle('nsa-wz-card--ghost', !name);

    var logoBlock = this.blocks.sb_salon_logo;
    var file = logoBlock && logoBlock.querySelector('input[type="file"]');
    var picked = file && file.files && file.files[0] ? file.files[0] : null;

    if (picked && this.pvLogoName !== picked.name) {
      this.pvLogoName = picked.name;
      if (this.pvLogoUrl) URL.revokeObjectURL(this.pvLogoUrl);
      this.pvLogoUrl = URL.createObjectURL(picked);
      this.pv.logo.style.backgroundImage = 'url("' + this.pvLogoUrl + '")';
    } else if (!picked && this.pvLogoUrl) {
      URL.revokeObjectURL(this.pvLogoUrl);
      this.pvLogoUrl = null;
      this.pvLogoName = null;
      this.pv.logo.style.backgroundImage = '';
    }

    /* De beginletter is alleen een plaatsvervanger. Staat er een logo, dan moet
       de letter wég — anders schijnt hij er dwars doorheen. */
    this.pv.logo.textContent = this.pvLogoUrl
      ? ''
      : (name ? name.trim().charAt(0).toUpperCase() : '');

    this.pv.tags.textContent = '';
    treatments.split(',').map(function (s) { return s.trim(); }).filter(Boolean).slice(0, 4)
      .forEach(function (t) { this.pv.tags.appendChild(el('span', 'nsa-wz-card__tag', t)); }, this);
  };

  Wizard.prototype.onInput = function () {
    var before = this.previewSignature();
    this.renderPreview();
    if (before !== this.previewSignature()) this.bumpPreview();
    if (this.steps[this.index] && this.steps[this.index].def.id === 'confirm') this.renderSummary();
  };

  Wizard.prototype.previewSignature = function () {
    if (!this.pv) return '';
    return [this.pv.name.textContent, this.pv.place.textContent, this.pv.tags.textContent].join('|');
  };

  /* Kort op- en neergaan van de kaart, zonder de animatie te stapelen. */
  Wizard.prototype.bumpPreview = function () {
    var node = this.preview;
    if (!node) return;
    window.clearTimeout(this.bumpTimer);
    node.classList.remove('nsa-wz-preview--bump');
    void node.offsetWidth;
    node.classList.add('nsa-wz-preview--bump');
    this.bumpTimer = window.setTimeout(function () {
      node.classList.remove('nsa-wz-preview--bump');
    }, 450);
  };

  /* ── Validatie ─────────────────────────────────────────────── */

  Wizard.prototype.clearError = function (block) {
    block.classList.remove('nsa-wz-invalid');
    var e = block.querySelector('.nsa-wz-err');
    if (e) e.remove();
  };

  Wizard.prototype.setError = function (block, message) {
    this.clearError(block);
    block.classList.add('nsa-wz-invalid');
    block.appendChild(el('span', 'nsa-wz-err', message));
  };

  Wizard.prototype.messageFor = function (block, key) {
    if (key === 'ownership') return this.text.requiredConsent;
    if (block.querySelector('input[type="file"]')) return this.text.requiredFile;
    if (block.querySelector('input[type="radio"], input[type="checkbox"]')) return this.text.requiredChoice;
    return this.text.required;
  };

  Wizard.prototype.validateStep = function (stepIndex) {
    var self = this;
    var step = this.steps[stepIndex];
    if (!step) return true;
    var firstBad = null;

    Array.prototype.forEach.call(step.panel.children, function (block) {
      if (!block.classList || !isFieldBlock(block)) return;
      var key = keyOfBlock(block);
      self.clearError(block);
      var value = valueOf(block);

      if (REQUIRED[key] && !value) {
        self.setError(block, self.messageFor(block, key));
        if (!firstBad) firstBad = block;
        return;
      }
      if (!value) return;

      var shape = SHAPE[key];
      if (shape === 'email' && !RE_EMAIL.test(value)) {
        self.setError(block, self.text.badEmail);
        if (!firstBad) firstBad = block;
      } else if (shape === 'url') {
        if (!looksLikeUrl(value)) {
          self.setError(block, self.text.badUrl);
          if (!firstBad) firstBad = block;
        } else {
          var full = withScheme(value);
          var ctrl = block.querySelector('input, textarea');
          if (ctrl && ctrl.value !== full) setNativeValue(ctrl, full);
        }
      }
    });

    if (firstBad) {
      var ctrl = firstBad.querySelector('input, textarea, select');
      if (ctrl && ctrl.focus) ctrl.focus();
      return false;
    }
    return true;
  };

  /* ── Navigatie ─────────────────────────────────────────────── */

  Wizard.prototype.go = function (target, skipValidation) {
    if (target > this.index && !skipValidation && !this.validateStep(this.index)) return;
    if (target < 0 || target >= this.steps.length) return;

    if (this.steps[this.index] && this.steps[this.index].def.id === 'address' && target > this.index) {
      this.lookupGeo();
    }
    this.show(target, true);
  };

  Wizard.prototype.show = function (target, scroll) {
    var self = this;
    this.index = target;

    this.steps.forEach(function (s, i) {
      s.panel.hidden = i !== target;
      s.panel.classList.toggle('nsa-wz-step--entering', i === target);
      if (i !== target) return;
      /* Zichtbare velden krijgen een oplopende vertraging zodat ze na elkaar
         binnenkomen; verborgen velden tellen niet mee in die volgorde. */
      var order = 0;
      Array.prototype.forEach.call(s.panel.children, function (child) {
        if (child.hidden) return;
        child.style.setProperty('--nsa-i', order);
        order += 1;
      });

      /* De klasse weer weghalen zodra iedereen binnen is. Blijft hij staan,
         dan speelt de animatie opnieuw af bij elke klassewijziging op een
         kind — bijvoorbeeld als een foutmelding verdwijnt. */
      window.clearTimeout(self.enterTimer);
      self.enterTimer = window.setTimeout(function () {
        s.panel.classList.remove('nsa-wz-step--entering');
      }, 500 + order * 55);
    });

    /* De kop opnieuw laten binnenkomen: klasse weghalen, reflow forceren,
       weer toevoegen — anders speelt de animatie maar één keer af. */
    this.head.classList.remove('nsa-wz-head--in');
    void this.head.offsetWidth;
    this.head.classList.add('nsa-wz-head--in');

    Array.prototype.forEach.call(this.rail.children, function (seg, i) {
      seg.classList.toggle('nsa-wz-rail__item--done', i < target);
      seg.classList.toggle('nsa-wz-rail__item--current', i === target);
    });

    var def = this.steps[target].def;
    this.count.textContent = fill(this.text.stepOf, { n: target + 1, total: this.steps.length });
    this.title.textContent = this.text.stepTitles[def.id] || def.id;
    this.intro.textContent = this.text.stepTexts[def.id] || '';
    this.intro.hidden = !this.intro.textContent;

    var isLast = target === this.steps.length - 1;
    this.nextBtn.hidden = isLast;
    this.submit.hidden = !isLast;
    this.backBtn.hidden = target === 0;

    if (isLast) this.renderSummary();

    /* Alleen bij navigeren terugspringen naar de kop — niet bij het opstarten,
       anders scrollt de pagina bij het laden ongevraagd weg. */
    if (!scroll) return;
    window.requestAnimationFrame(function () {
      var host = self.host || self.root.host;
      if (host && host.scrollIntoView) host.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  /* ── Geocoding ─────────────────────────────────────────────── */

  Wizard.prototype.addressQuery = function () {
    var self = this;
    var parts = ['sb_salon_street', 'sb_salon_postal_code', 'sb_salon_city', 'sb_salon_state', 'sb_salon_country']
      .map(function (k) { return self.blocks[k] ? valueOf(self.blocks[k]) : ''; })
      .filter(Boolean);
    return parts.join(', ');
  };

  Wizard.prototype.setGeoMessage = function (state, message) {
    if (!this.geoBox) return;
    this.geoBox.classList.toggle('nsa-wz-geo--busy', state === 'busy');
    this.geoBox.classList.toggle('nsa-wz-geo--found', state === 'found');
    var msg = this.geoBox.querySelector('.nsa-wz-geo__msg');
    if (msg) msg.textContent = message || '';
  };

  Wizard.prototype.applyGeo = function () {
    var lat = this.blocks.sb_salon_lat && this.blocks.sb_salon_lat.querySelector('input');
    var lng = this.blocks.sb_salon_lng && this.blocks.sb_salon_lng.querySelector('input');
    if (lat && this.geo.lat) setNativeValue(lat, this.geo.lat);
    if (lng && this.geo.lng) setNativeValue(lng, this.geo.lng);
  };

  Wizard.prototype.lookupGeo = function () {
    var self = this;
    var query = this.addressQuery();
    if (!query || query === this.geo.signature) return;
    this.geo.signature = query;
    this.setGeoMessage('busy', this.text.geoChecking);

    this.geocode(query).then(function (hit) {
      if (!hit) {
        self.geo.lat = self.geo.lng = self.geo.place = '';
        self.setGeoMessage('failed', self.text.geoFailed);
        return;
      }
      self.geo.lat = String(hit.lat);
      self.geo.lng = String(hit.lng);
      self.geo.place = hit.place;
      self.applyGeo();
      self.setGeoMessage('found', fill(self.text.geoFound, { place: hit.place }));
    }).catch(function () {
      self.setGeoMessage('failed', self.text.geoFailed);
    });
  };

  /* Twee sleutelloze OpenStreetMap-diensten; Photon eerst, Nominatim als reserve. */
  Wizard.prototype.geocode = function (query) {
    return fetch('https://photon.komoot.io/api/?limit=1&q=' + encodeURIComponent(query))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var f = data && data.features && data.features[0];
        if (!f || !f.geometry || !f.geometry.coordinates) return null;
        var p = f.properties || {};
        var street = [p.street, p.housenumber].filter(Boolean).join(' ');
        return {
          lat: f.geometry.coordinates[1],
          lng: f.geometry.coordinates[0],
          place: [p.name || street, p.city, p.country].filter(Boolean).join(', ') || query
        };
      })
      .catch(function () { return null; })
      .then(function (hit) {
        if (hit) return hit;
        return fetch('https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=' + encodeURIComponent(query))
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (rows) {
            var row = rows && rows[0];
            if (!row) return null;
            return { lat: row.lat, lng: row.lon, place: row.display_name || query };
          })
          .catch(function () { return null; });
      });
  };

  /* ───────────────────────────────────────────────────────────────
     Opstarten
     ─────────────────────────────────────────────────────────────── */

  function styleEmbed(embed) {
    var root = embed.shadowRoot;
    if (!root || embed.hasAttribute(MARK)) return;
    if ('adoptedStyleSheets' in root) {
      root.adoptedStyleSheets = root.adoptedStyleSheets.concat(buildSheet());
    }
    embed.setAttribute(MARK, '');
  }

  function enhance(embed) {
    var root = embed.shadowRoot;
    if (!root) return;
    styleEmbed(embed);
    var host = embed.closest ? embed.closest('.nsa-form-host') : null;
    try {
      new Wizard(root, host).build();
    } catch (e) {
      /* Mislukt de wizard, dan blijft het gewone formulier gewoon werken. */
      if (window.console && window.console.warn) window.console.warn('[nimbus] salon wizard skipped:', e);
    }
  }

  /* De app voegt <shopify-forms-embed> pas ná ons script in, en de shadow root
     bestaat pas na het upgraden van dat custom element. We kijken daarom niet
     naar de embed maar naar de host — die staat er wél meteen — en wachten
     binnen een ruime tijdslimiet tot het formulier erin verschijnt. */
  function watchHost(host) {
    if (host.hasAttribute(WATCH)) return;
    host.setAttribute(WATCH, '');

    var tries = 0;
    var timer = window.setInterval(function () {
      var embed = host.querySelector(EMBED);
      if (embed && embed.shadowRoot && embed.shadowRoot.querySelector('form')) {
        window.clearInterval(timer);
        enhance(embed);
        return;
      }
      if (++tries >= POLL_MAX) window.clearInterval(timer);
    }, POLL_MS);
  }

  function init(scope) {
    var hosts = (scope || document).querySelectorAll('.nsa-form-host');
    Array.prototype.forEach.call(hosts, watchHost);
  }

  init();
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
